import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BookingSummary = ({ eventData, selectedTier, quantity, discountApplied }) => {
  const basePrice = selectedTier?.price * quantity;
  const discountAmount = discountApplied 
    ? discountApplied?.type === 'percentage' 
      ? (basePrice * discountApplied?.discount) / 100
      : discountApplied?.discount
    : 0;
  const subtotal = basePrice - discountAmount;
  const processingFee = subtotal * 0.029; // 2.9% processing fee
  const total = subtotal + processingFee;

  return (
    <div className="bg-background border border-border rounded-lg p-6 sticky top-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Booking Summary</h3>
      {/* Event Details */}
      <div className="space-y-4 mb-6">
        <div className="flex space-x-3">
          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
            <Image
              src={eventData?.image}
              alt={eventData?.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-foreground truncate">{eventData?.title}</h4>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground mt-1">
              <Icon name="Calendar" size={14} />
              <span>{eventData?.date}</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="MapPin" size={14} />
              <span>{eventData?.location}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Ticket Details */}
      <div className="space-y-3 mb-6 pb-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-foreground">{selectedTier?.name}</div>
            <div className="text-sm text-muted-foreground">
              ${selectedTier?.price?.toFixed(2)} × {quantity} ticket{quantity > 1 ? 's' : ''}
            </div>
          </div>
          <div className="text-right">
            <div className="font-medium text-foreground">${basePrice?.toFixed(2)}</div>
          </div>
        </div>

        {/* Tier Benefits */}
        <div className="space-y-1">
          {selectedTier?.benefits?.slice(0, 3)?.map((benefit, index) => (
            <div key={index} className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Check" size={12} className="text-success" />
              <span>{benefit}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Pricing Breakdown */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="text-foreground">${basePrice?.toFixed(2)}</span>
        </div>

        {discountApplied && (
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2">
              <span className="text-success">Discount ({discountApplied?.description})</span>
              <Icon name="Tag" size={12} className="text-success" />
            </div>
            <span className="text-success">-${discountAmount?.toFixed(2)}</span>
          </div>
        )}

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <span className="text-muted-foreground">Processing Fee</span>
            <div className="group relative">
              <Icon name="Info" size={12} className="text-muted-foreground cursor-help" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-foreground text-background text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                2.9% processing fee
              </div>
            </div>
          </div>
          <span className="text-foreground">${processingFee?.toFixed(2)}</span>
        </div>

        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-foreground">Total</span>
            <span className="text-lg font-semibold text-foreground">${total?.toFixed(2)}</span>
          </div>
        </div>
      </div>
      {/* Security & Guarantee */}
      <div className="space-y-3 text-xs text-muted-foreground">
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={12} className="text-success" />
          <span>Secure payment processing</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="RefreshCw" size={12} className="text-success" />
          <span>Free cancellation up to 24 hours before event</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Smartphone" size={12} className="text-success" />
          <span>Mobile tickets delivered instantly</span>
        </div>
      </div>
      {/* Event Organizer */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full overflow-hidden">
            <Image
              src={eventData?.organizer?.avatar}
              alt={eventData?.organizer?.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <div className="text-sm font-medium text-foreground">{eventData?.organizer?.name}</div>
            <div className="text-xs text-muted-foreground">Event Organizer</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;